import { useState, useEffect } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import {
  ALL_TYPE_CODES,
  TYPE_INTERPRETATIONS,
  TYPE_COLORS,
  DIMENSION_INFO,
  compareTypes,
  getRelationshipColor,
  type TypeCode,
  type TypeComparison,
} from '@mindtype/shared'
import { CompareShareCard } from './components/CompareShareCard'

// 维度简称映射
const DIMENSION_SHORT: Record<string, { A: string; B: string }> = {
  D1: { A: '内部', B: '外部' },
  D2: { A: '系统', B: '情境' },
  D3: { A: '内在', B: '外在' },
  D4: { A: '闭合', B: '探索' },
}

type SelectMode = 'dropdown' | 'dimension'

interface DimensionSelection {
  D1: 'A' | 'R' | null
  D2: 'S' | 'C' | null
  D3: 'I' | 'E' | null
  D4: 'D' | 'O' | null
}

export function TypeComparePage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [typeA, setTypeA] = useState<TypeCode | null>(null)
  const [typeB, setTypeB] = useState<TypeCode | null>(null)
  const [selectModeA, setSelectModeA] = useState<SelectMode>('dropdown')
  const [selectModeB, setSelectModeB] = useState<SelectMode>('dropdown')
  const [dimensionSelectionA, setDimensionSelectionA] = useState<DimensionSelection>({
    D1: null, D2: null, D3: null, D4: null
  })
  const [dimensionSelectionB, setDimensionSelectionB] = useState<DimensionSelection>({
    D1: null, D2: null, D3: null, D4: null
  })
  const [comparison, setComparison] = useState<TypeComparison | null>(null)
  const [showShareCard, setShowShareCard] = useState(false)

  // 从 URL 参数初始化
  useEffect(() => {
    const a = searchParams.get('a')?.toUpperCase()
    const b = searchParams.get('b')?.toUpperCase()

    if (a && ALL_TYPE_CODES.includes(a as TypeCode)) {
      setTypeA(a as TypeCode)
    }
    if (b && ALL_TYPE_CODES.includes(b as TypeCode)) {
      setTypeB(b as TypeCode)
    }
  }, [searchParams])

  // 当两个类型都选择后，进行对比
  useEffect(() => {
    if (typeA && typeB) {
      const result = compareTypes(typeA, typeB)
      setComparison(result)

      // 更新 URL
      setSearchParams({ a: typeA, b: typeB })
    } else {
      setComparison(null)
    }
  }, [typeA, typeB, setSearchParams])

  // 从维度选择生成类型代码
  const getTypeFromDimensions = (selection: DimensionSelection): TypeCode | null => {
    if (selection.D1 && selection.D2 && selection.D3 && selection.D4) {
      return `${selection.D1}${selection.D2}${selection.D3}${selection.D4}` as TypeCode
    }
    return null
  }

  // 更新维度选择 A
  const handleDimensionChangeA = (dim: keyof DimensionSelection, value: string) => {
    const newSelection = { ...dimensionSelectionA, [dim]: value }
    setDimensionSelectionA(newSelection)
    const typeCode = getTypeFromDimensions(newSelection)
    if (typeCode) {
      setTypeA(typeCode)
    }
  }

  // 更新维度选择 B
  const handleDimensionChangeB = (dim: keyof DimensionSelection, value: string) => {
    const newSelection = { ...dimensionSelectionB, [dim]: value }
    setDimensionSelectionB(newSelection)
    const typeCode = getTypeFromDimensions(newSelection)
    if (typeCode) {
      setTypeB(typeCode)
    }
  }

  // 交换两个类型
  const handleSwap = () => {
    const tempType = typeA
    setTypeA(typeB)
    setTypeB(tempType)
  }

  // 重置
  const handleReset = () => {
    setTypeA(null)
    setTypeB(null)
    setDimensionSelectionA({ D1: null, D2: null, D3: null, D4: null })
    setDimensionSelectionB({ D1: null, D2: null, D3: null, D4: null })
    setSearchParams({})
  }

  const colorA = typeA ? TYPE_COLORS[typeA] : { primary: '#6366f1', secondary: '#818cf8', text: '#ffffff' }
  const colorB = typeB ? TYPE_COLORS[typeB] : { primary: '#8b5cf6', secondary: '#a78bfa', text: '#ffffff' }

  // 混合两种类型颜色的渐变
  const gradientStyle = typeA && typeB ? {
    background: `linear-gradient(135deg, ${colorA.primary}15 0%, ${colorB.primary}15 100%)`,
  } : {}

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* 页面头部 */}
        <header className="text-center mb-8">
          <Link to="/" className="text-sm text-gray-500 hover:text-gray-700 mb-4 inline-block">
            ← 返回首页
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">类型对比</h1>
          <p className="text-gray-500 mt-2">选择两个认知类型，了解相处模式与沟通建议</p>
        </header>

        {/* 类型选择区域 */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6 relative">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* 类型 A 选择器 */}
            <div
              className="p-4 rounded-lg transition-all"
              style={{
                backgroundColor: typeA ? `${colorA.primary}05` : 'transparent',
                border: typeA ? `2px solid ${colorA.primary}30` : '2px solid transparent',
              }}
            >
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium" style={{ color: typeA ? colorA.primary : '#374151' }}>
                  类型 A
                </label>
                <button
                  onClick={() => setSelectModeA(selectModeA === 'dropdown' ? 'dimension' : 'dropdown')}
                  className="text-xs hover:opacity-80"
                  style={{ color: typeA ? colorA.primary : '#6366f1' }}
                >
                  {selectModeA === 'dropdown' ? '按维度选择' : '选择类型代码'}
                </button>
              </div>

              {selectModeA === 'dropdown' ? (
                <select
                  value={typeA || ''}
                  onChange={(e) => setTypeA(e.target.value as TypeCode || null)}
                  className="w-full px-4 py-3 border rounded-lg text-gray-900 focus:outline-none"
                  style={{
                    borderColor: typeA ? colorA.primary : '#e5e7eb',
                    boxShadow: typeA ? `0 0 0 2px ${colorA.primary}20` : 'none',
                  }}
                >
                  <option value="">选择类型...</option>
                  {ALL_TYPE_CODES.map((code) => {
                    const info = TYPE_INTERPRETATIONS[code]
                    return (
                      <option key={code} value={code}>
                        {code} - {info?.name || code}
                      </option>
                    )
                  })}
                </select>
              ) : (
                <div className="space-y-2">
                  {(['D1', 'D2', 'D3', 'D4'] as const).map((dim) => {
                    const info = DIMENSION_INFO[dim]
                    return (
                      <div key={dim} className="flex items-center gap-2">
                        <span className="w-16 text-xs text-gray-500">{info.name}</span>
                        <div className="flex-1 flex gap-1">
                          <button
                            onClick={() => handleDimensionChangeA(dim, info.poleA.letter)}
                            className="flex-1 py-1.5 text-xs rounded transition-colors"
                            style={{
                              backgroundColor: dimensionSelectionA[dim] === info.poleA.letter ? colorA.primary : '#f3f4f6',
                              color: dimensionSelectionA[dim] === info.poleA.letter ? colorA.text : '#4b5563',
                            }}
                          >
                            {info.poleA.letter}
                          </button>
                          <button
                            onClick={() => handleDimensionChangeA(dim, info.poleB.letter)}
                            className="flex-1 py-1.5 text-xs rounded transition-colors"
                            style={{
                              backgroundColor: dimensionSelectionA[dim] === info.poleB.letter ? colorA.primary : '#f3f4f6',
                              color: dimensionSelectionA[dim] === info.poleB.letter ? colorA.text : '#4b5563',
                            }}
                          >
                            {info.poleB.letter}
                          </button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}

              {typeA && (
                <div className="mt-3 flex items-center gap-2">
                  <span
                    className="px-3 py-1.5 rounded font-mono font-bold text-sm"
                    style={{ backgroundColor: colorA.primary, color: colorA.text }}
                  >
                    {typeA}
                  </span>
                  <span className="text-sm font-medium" style={{ color: colorA.primary }}>
                    {TYPE_INTERPRETATIONS[typeA]?.name}
                  </span>
                </div>
              )}
            </div>

            {/* 中间的 VS */}
            <div className="hidden md:flex items-center justify-center absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
              <div className="w-12 h-12 bg-white border-2 border-gray-200 rounded-full flex items-center justify-center text-gray-400 font-bold text-sm shadow-sm">
                VS
              </div>
            </div>

            {/* 类型 B 选择器 */}
            <div
              className="p-4 rounded-lg transition-all"
              style={{
                backgroundColor: typeB ? `${colorB.primary}05` : 'transparent',
                border: typeB ? `2px solid ${colorB.primary}30` : '2px solid transparent',
              }}
            >
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium" style={{ color: typeB ? colorB.primary : '#374151' }}>
                  类型 B
                </label>
                <button
                  onClick={() => setSelectModeB(selectModeB === 'dropdown' ? 'dimension' : 'dropdown')}
                  className="text-xs hover:opacity-80"
                  style={{ color: typeB ? colorB.primary : '#8b5cf6' }}
                >
                  {selectModeB === 'dropdown' ? '按维度选择' : '选择类型代码'}
                </button>
              </div>

              {selectModeB === 'dropdown' ? (
                <select
                  value={typeB || ''}
                  onChange={(e) => setTypeB(e.target.value as TypeCode || null)}
                  className="w-full px-4 py-3 border rounded-lg text-gray-900 focus:outline-none"
                  style={{
                    borderColor: typeB ? colorB.primary : '#e5e7eb',
                    boxShadow: typeB ? `0 0 0 2px ${colorB.primary}20` : 'none',
                  }}
                >
                  <option value="">选择类型...</option>
                  {ALL_TYPE_CODES.map((code) => {
                    const info = TYPE_INTERPRETATIONS[code]
                    return (
                      <option key={code} value={code}>
                        {code} - {info?.name || code}
                      </option>
                    )
                  })}
                </select>
              ) : (
                <div className="space-y-2">
                  {(['D1', 'D2', 'D3', 'D4'] as const).map((dim) => {
                    const info = DIMENSION_INFO[dim]
                    return (
                      <div key={dim} className="flex items-center gap-2">
                        <span className="w-16 text-xs text-gray-500">{info.name}</span>
                        <div className="flex-1 flex gap-1">
                          <button
                            onClick={() => handleDimensionChangeB(dim, info.poleA.letter)}
                            className="flex-1 py-1.5 text-xs rounded transition-colors"
                            style={{
                              backgroundColor: dimensionSelectionB[dim] === info.poleA.letter ? colorB.primary : '#f3f4f6',
                              color: dimensionSelectionB[dim] === info.poleA.letter ? colorB.text : '#4b5563',
                            }}
                          >
                            {info.poleA.letter}
                          </button>
                          <button
                            onClick={() => handleDimensionChangeB(dim, info.poleB.letter)}
                            className="flex-1 py-1.5 text-xs rounded transition-colors"
                            style={{
                              backgroundColor: dimensionSelectionB[dim] === info.poleB.letter ? colorB.primary : '#f3f4f6',
                              color: dimensionSelectionB[dim] === info.poleB.letter ? colorB.text : '#4b5563',
                            }}
                          >
                            {info.poleB.letter}
                          </button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}

              {typeB && (
                <div className="mt-3 flex items-center gap-2">
                  <span
                    className="px-3 py-1.5 rounded font-mono font-bold text-sm"
                    style={{ backgroundColor: colorB.primary, color: colorB.text }}
                  >
                    {typeB}
                  </span>
                  <span className="text-sm font-medium" style={{ color: colorB.primary }}>
                    {TYPE_INTERPRETATIONS[typeB]?.name}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* 移动端交换和重置按钮 */}
          {(typeA || typeB) && (
            <div className="flex justify-center gap-3 mt-4">
              {typeA && typeB && (
                <button
                  onClick={handleSwap}
                  className="px-4 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  交换位置
                </button>
              )}
              <button
                onClick={handleReset}
                className="px-4 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                重置
              </button>
            </div>
          )}
        </div>

        {/* 对比结果区域 */}
        {comparison && typeA && typeB && (
          <div className="space-y-6">
            {/* 关系类型标签 - 使用两种类型颜色的渐变 */}
            <div
              className="rounded-xl p-6 text-center"
              style={gradientStyle}
            >
              {/* 双色类型展示 */}
              <div className="flex items-center justify-center gap-4 mb-4">
                <div className="text-center">
                  <span
                    className="inline-block px-4 py-2 rounded-lg font-mono font-bold text-lg"
                    style={{ backgroundColor: colorA.primary, color: colorA.text }}
                  >
                    {typeA}
                  </span>
                  <p className="text-sm mt-1" style={{ color: colorA.primary }}>
                    {TYPE_INTERPRETATIONS[typeA]?.name}
                  </p>
                </div>
                <span className="text-gray-400 text-xl font-light">×</span>
                <div className="text-center">
                  <span
                    className="inline-block px-4 py-2 rounded-lg font-mono font-bold text-lg"
                    style={{ backgroundColor: colorB.primary, color: colorB.text }}
                  >
                    {typeB}
                  </span>
                  <p className="text-sm mt-1" style={{ color: colorB.primary }}>
                    {TYPE_INTERPRETATIONS[typeB]?.name}
                  </p>
                </div>
              </div>

              <h2
                className="text-xl font-bold mb-2"
                style={{ color: getRelationshipColor(comparison.relationshipType) }}
              >
                {comparison.relationshipType}
              </h2>
              <p className="text-gray-600 text-sm leading-relaxed max-w-lg mx-auto">
                {comparison.relationshipAnalysis}
              </p>
            </div>

            {/* 维度对比图 */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-base font-semibold text-gray-900 mb-4">维度对比</h3>

              {/* 类型标识 */}
              <div className="flex justify-center gap-6 mb-6">
                <div className="flex items-center gap-2">
                  <div
                    className="w-4 h-4 rounded"
                    style={{ backgroundColor: colorA.primary }}
                  />
                  <span className="text-sm font-bold" style={{ color: colorA.primary }}>
                    {typeA}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div
                    className="w-4 h-4 rounded"
                    style={{ backgroundColor: colorB.primary }}
                  />
                  <span className="text-sm font-bold" style={{ color: colorB.primary }}>
                    {typeB}
                  </span>
                </div>
              </div>

              {/* 四维度对比 */}
              <div className="space-y-4">
                {comparison.dimensionComparison.map((dim) => {
                  const shortNames = DIMENSION_SHORT[dim.dimension]
                  const info = DIMENSION_INFO[dim.dimension]

                  // 计算位置（A极在左，B极在右）
                  const typeAIsLeft = dim.typeALetter === info.poleA.letter
                  const typeBIsLeft = dim.typeBLetter === info.poleA.letter
                  const typeAPos = typeAIsLeft ? 20 : 80
                  const typeBPos = typeBIsLeft ? 20 : 80

                  // 根据是否相同决定颜色强度
                  const colorADisplay = dim.isSame ? colorA.primary : `${colorA.primary}70`
                  const colorBDisplay = dim.isSame ? colorB.primary : `${colorB.primary}70`

                  // 相同维度使用双色渐变背景
                  const bgStyle = dim.isSame
                    ? { background: `linear-gradient(135deg, ${colorA.primary}12 0%, ${colorB.primary}12 100%)` }
                    : { backgroundColor: '#f9fafb' }

                  // 相同维度边框也使用渐变效果
                  const borderStyle = dim.isSame
                    ? `2px solid ${colorA.primary}40`
                    : '1px solid #e5e7eb'

                  return (
                    <div
                      key={dim.dimension}
                      className="p-3 rounded-lg"
                      style={{
                        ...bgStyle,
                        border: borderStyle,
                      }}
                    >
                      <div className="flex justify-between text-xs mb-2">
                        <span style={{
                          color: dim.isSame
                            ? (typeAIsLeft ? colorA.primary : colorB.primary)
                            : '#9ca3af',
                          fontWeight: dim.isSame ? 600 : 400,
                        }}>
                          {shortNames.A}
                        </span>
                        <span
                          className="font-medium"
                          style={{
                            color: dim.isSame ? colorA.primary : '#9ca3af',
                          }}
                        >
                          {dim.dimensionName}
                        </span>
                        <span style={{
                          color: dim.isSame
                            ? (!typeAIsLeft ? colorA.primary : colorB.primary)
                            : '#9ca3af',
                          fontWeight: dim.isSame ? 600 : 400,
                        }}>
                          {shortNames.B}
                        </span>
                      </div>
                      <div
                        className="relative h-3 rounded-full"
                        style={{
                          backgroundColor: dim.isSame ? `${colorA.primary}15` : '#e5e7eb',
                        }}
                      >
                        {/* 相同维度时显示连接条 */}
                        {dim.isSame && (
                          <div
                            className="absolute top-1/2 -translate-y-1/2 h-2 rounded-full"
                            style={{
                              left: `calc(${Math.min(typeAPos, typeBPos)}% - 10px)`,
                              right: `calc(${100 - Math.max(typeAPos, typeBPos)}% - 10px)`,
                              background: `linear-gradient(90deg, ${colorA.primary} 0%, ${colorB.primary} 100%)`,
                            }}
                          />
                        )}
                        {/* 类型 A 标记 */}
                        <div
                          className="absolute top-1/2 -translate-y-1/2 w-5 h-5 rounded border-2 border-white shadow-md z-10"
                          style={{
                            left: `calc(${typeAPos}% - 10px)`,
                            backgroundColor: colorADisplay,
                          }}
                        />
                        {/* 类型 B 标记 */}
                        <div
                          className="absolute top-1/2 -translate-y-1/2 w-5 h-5 rounded border-2 border-white shadow-md z-10"
                          style={{
                            left: `calc(${typeBPos}% - 10px)`,
                            backgroundColor: colorBDisplay,
                          }}
                        />
                      </div>
                    </div>
                  )
                })}
              </div>

              <p className="text-xs text-gray-500 text-center mt-4">
                相同维度数：{comparison.sameCount} / 4
              </p>
            </div>

            {/* 共同点与差异点 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* 共同点 */}
              {comparison.commonPoints.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                  <h3 className="text-base font-semibold text-green-700 mb-3">
                    你们的共同点
                  </h3>
                  <ul className="space-y-2">
                    {comparison.commonPoints.map((point, i) => (
                      <li key={i} className="text-sm text-gray-600 flex items-start gap-2">
                        <span className="text-green-500 mt-0.5">•</span>
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* 差异点 */}
              {comparison.differences.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                  <h3 className="text-base font-semibold text-amber-700 mb-3">
                    你们的差异点
                  </h3>
                  <ul className="space-y-2">
                    {comparison.differences.map((diff, i) => (
                      <li key={i} className="text-sm text-gray-600 flex items-start gap-2">
                        <span className="text-amber-500 mt-0.5">•</span>
                        <span>{diff}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* 沟通建议 */}
            {comparison.adviceForA.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h3 className="text-base font-semibold text-gray-900 mb-4">
                  沟通建议
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* 给类型 A 的建议 */}
                  <div
                    className="p-4 rounded-lg"
                    style={{ backgroundColor: `${colorA.primary}08`, border: `2px solid ${colorA.primary}20` }}
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <span
                        className="px-2 py-0.5 rounded text-xs font-bold"
                        style={{ backgroundColor: colorA.primary, color: colorA.text }}
                      >
                        {typeA}
                      </span>
                      <span className="text-sm" style={{ color: colorA.primary }}>与 {typeB} 相处时</span>
                    </div>
                    <ul className="space-y-2">
                      {comparison.adviceForA.map((advice, i) => (
                        <li key={i} className="text-sm text-gray-700 leading-relaxed">
                          {advice}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* 给类型 B 的建议 */}
                  <div
                    className="p-4 rounded-lg"
                    style={{ backgroundColor: `${colorB.primary}08`, border: `2px solid ${colorB.primary}20` }}
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <span
                        className="px-2 py-0.5 rounded text-xs font-bold"
                        style={{ backgroundColor: colorB.primary, color: colorB.text }}
                      >
                        {typeB}
                      </span>
                      <span className="text-sm" style={{ color: colorB.primary }}>与 {typeA} 相处时</span>
                    </div>
                    <ul className="space-y-2">
                      {comparison.adviceForB.map((advice, i) => (
                        <li key={i} className="text-sm text-gray-700 leading-relaxed">
                          {advice}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* 分享区域 - 使用双色渐变 */}
            <div
              className="rounded-xl p-6 text-center"
              style={gradientStyle}
            >
              <p className="text-gray-600 mb-4">
                分享对比结果给朋友，一起探索认知风格
              </p>
              <div className="flex justify-center gap-3">
                <button
                  onClick={() => {
                    const url = `${window.location.origin}/compare?a=${typeA}&b=${typeB}`
                    navigator.clipboard.writeText(url)
                    alert('链接已复制到剪贴板')
                  }}
                  className="px-5 py-2.5 bg-white text-gray-700 border border-gray-200 rounded-lg font-medium transition-colors hover:bg-gray-50"
                >
                  复制链接
                </button>
                <button
                  onClick={() => setShowShareCard(true)}
                  className="px-5 py-2.5 text-white rounded-lg font-medium transition-opacity hover:opacity-90"
                  style={{
                    background: `linear-gradient(135deg, ${colorA.primary} 0%, ${colorB.primary} 100%)`,
                  }}
                >
                  生成分享图片
                </button>
              </div>
            </div>

            {/* 分享卡片弹窗 */}
            {showShareCard && (
              <CompareShareCard
                typeA={typeA}
                typeB={typeB}
                comparison={comparison}
                onClose={() => setShowShareCard(false)}
              />
            )}
          </div>
        )}

        {/* 未选择时的提示 */}
        {!comparison && (
          <div className="text-center py-12 text-gray-500">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
              <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
              </svg>
            </div>
            <p>选择两个类型开始对比</p>
            <p className="text-sm mt-2">可以对比你和朋友、同事、伴侣的认知类型</p>
          </div>
        )}

        {/* 版权声明 */}
        <footer className="text-center text-xs text-gray-400 py-6 mt-8 border-t border-gray-100">
          <p>© 2024 MindType</p>
          <p className="mt-1">内容受版权保护，未经授权禁止商业使用</p>
        </footer>
      </div>
    </div>
  )
}
